import { useState, useEffect } from 'react'
import { Box, Button, Icon, Modal } from 'components/atomic'

interface GifPickerModalProps {
  isOpen: boolean
  onClose: () => void
  onSelectGif: (gifUrl: string) => void
}

interface TenorGif {
  id: string
  media_formats: {
    gif: { url: string }
    tinygif: { url: string }
  }
}

export const GifPickerModal = ({ isOpen, onClose, onSelectGif }: GifPickerModalProps) => {
  const [searchQuery, setSearchQuery] = useState<string>('')
  const [gifs, setGifs] = useState<TenorGif[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [selectedCategory, setSelectedCategory] = useState<string>('trending')

  // Tenor API key from environment variables
  const TENOR_API_KEY = import.meta.env.VITE_TENOR_API_KEY
  const CLIENT_KEY = 'cyberlete'

  const categories = [
    'trending',
    'sports',
    'gaming',
    'reactions',
    'memes',
    'celebrate',
    'excited'
  ]

  // Fetch trending or search GIFs
  const fetchGifs = async (query: string = '', category: string = 'trending') => {
    setLoading(true)
    try {
      let url = ''
      if (query.trim()) {
        // Search GIFs
        url = `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(query)}&key=${TENOR_API_KEY}&client_key=${CLIENT_KEY}&limit=20`
      } else {
        // Get trending or category GIFs
        const searchTerm = category === 'trending' ? '' : category
        url = searchTerm
          ? `https://tenor.googleapis.com/v2/search?q=${encodeURIComponent(searchTerm)}&key=${TENOR_API_KEY}&client_key=${CLIENT_KEY}&limit=20`
          : `https://tenor.googleapis.com/v2/featured?key=${TENOR_API_KEY}&client_key=${CLIENT_KEY}&limit=20`
      }

      const response = await fetch(url)
      const data = await response.json()
      setGifs(data.results || [])
    } catch (error) {
      console.error('Error fetching GIFs:', error)
      setGifs([])
    } finally {
      setLoading(false)
    }
  }

  // Load trending GIFs on mount
  useEffect(() => {
    if (isOpen) {
      fetchGifs('', selectedCategory)
    }
  }, [isOpen, selectedCategory])

  // Search handler with debounce
  useEffect(() => {
    if (!searchQuery.trim()) {
      fetchGifs('', selectedCategory)
      return
    }

    const timeoutId = setTimeout(() => {
      fetchGifs(searchQuery)
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [searchQuery])

  const handleSelectGif = (gif: TenorGif) => {
    onSelectGif(gif.media_formats.gif.url)
    onClose()
  }

  return (
    <Modal
      title="Choose a GIF"
      isOpened={isOpen}
      onClose={onClose}
    >
      <Box col className="w-full">
        {/* Search Bar */}
        <Box className="mb-4">
          <input
            type="text"
            placeholder="Search for GIFs..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-dark-bg-secondary border border-dark-border rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-green"
          />
        </Box>

        {/* Categories */}
        <Box className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {categories.map((category) => (
            <Button
              key={category}
              size="xs"
              variant={selectedCategory === category ? 'primary' : 'secondary'}
              onClick={() => {
                setSelectedCategory(category)
                setSearchQuery('')
              }}
              className="capitalize whitespace-nowrap"
            >
              {category}
            </Button>
          ))}
        </Box>

        {/* GIF Grid */}
        <Box className="relative" style={{ minHeight: '400px', maxHeight: '60vh' }}>
          {loading ? (
            <Box center className="py-8">
              <Icon name="spinner" size={32} color="green" className="animate-spin" />
            </Box>
          ) : (
            <Box className="grid grid-cols-2 md:grid-cols-3 gap-2 overflow-y-auto" style={{ maxHeight: '60vh' }}>
              {gifs.length > 0 ? (
                gifs.map((gif) => (
                  <Box
                    key={gif.id}
                    className="cursor-pointer rounded-lg overflow-hidden hover:opacity-80 transition-opacity border border-dark-border hover:border-green"
                    onClick={() => handleSelectGif(gif)}
                  >
                    <img
                      src={gif.media_formats.tinygif.url}
                      alt="GIF"
                      className="w-full h-auto object-cover"
                      style={{ maxHeight: '150px' }}
                    />
                  </Box>
                ))
              ) : (
                <Box col center className="py-8 col-span-full">
                  <Icon name="image" size={48} color="gray" />
                  <p className="text-gray-400 mt-2">No GIFs found</p>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Powered by Tenor */}
        <Box center className="mt-4 pt-4 border-t border-dark-border">
          <a
            href="https://tenor.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-400"
          >
            Powered by Tenor
          </a>
        </Box>
      </Box>
    </Modal>
  )
}
